import React, { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Button, Input } from '@column/column-ui-kit';

import { PopoverAction, PopoverBase, PopoverBaseProps } from '~/components/Popover/Base';
import { FormElement, FormLabel } from '~/styles';

interface PopoverFilterTextProps extends PopoverBaseProps {
  value?: string;
  label: string;
  placeholder?: string;
  onSubmit?: (value: string | undefined) => void;
}

const Wrapper = styled(PopoverBase)`
  --popover-padding: 0px;
  --popover-width: 248px;
  --popover-left: 0px;
`;

const Grid = styled.div`
  display: grid;
  padding: 12px 12px 0 12px;
`;

export const PopoverFilterText: React.FC<PopoverFilterTextProps> = ({
  value,
  label,
  placeholder,
  onSubmit,
  ...popoverProps
}) => {
  const [current, setCurrent] = useState<string>(value ?? '');

  useEffect(() => {
    setCurrent(value ?? '');
  }, [value]);

  const handleResetClick = useCallback(() => {
    setCurrent('');
    onSubmit?.(undefined);
  }, [onSubmit]);

  const handleApplyClick = useCallback(() => {
    const trimmed = current.trim();
    onSubmit?.(trimmed === '' ? undefined : trimmed);
  }, [current, onSubmit]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleApplyClick();
      }
    },
    [handleApplyClick]
  );

  return (
    <Wrapper {...popoverProps}>
      <Grid>
        <FormElement>
          <FormLabel>{label}</FormLabel>
          <Input
            size="small"
            value={current}
            placeholder={placeholder}
            onChange={(next: string) => setCurrent(next)}
            onKeyDown={handleKeyDown}
          />
        </FormElement>
      </Grid>
      <PopoverAction>
        <Button size="small" variant="secondary" onClick={handleResetClick}>
          Reset
        </Button>
        <Button size="small" onClick={handleApplyClick}>
          Apply
        </Button>
      </PopoverAction>
    </Wrapper>
  );
};
